import { useState } from "react";
import { motion } from "motion/react";
import {
  GraduationCap,
  BookOpen,
  MapPin,
  DollarSign,
  Clock,
  Sparkles,
  Check,
  ChevronRight,
  Star,
  ExternalLink,
  User,
  ArrowRight,
  MessageCircle,
} from "lucide-react";
import type { Answers } from "../../types/CourseFinder";
import {
  COURSE_DIRECTIONS,
  INTERESTS,
  QUALIFICATIONS,
  BUDGETS,
  PATHWAYS,
  TIMELINES,
} from "../../Data/studentData";
import { PartnerUniData, PartnerUniProps } from "../../Data/partnerUniData";

// ─── HELPERS ─────────────────────────────────────────────────────────────────
export function getRecommendedCourses(
  interests: string[],
): Array<{ name: string; desc: string }> {
  const all: Array<{ name: string; desc: string }> = [];
  const seen = new Set<string>();
  for (const id of interests) {
    for (const c of COURSE_DIRECTIONS[id] || []) {
      if (!seen.has(c.name)) {
        seen.add(c.name);
        all.push(c);
      }
    }
    if (all.length >= 6) break;
  }
  return all.length > 0 ? all.slice(0, 6) : COURSE_DIRECTIONS["not_sure"];
}

export function getRecommendedUniversities(
  pathway: string,
  budget: string,
  interests: string[],
) {
  let unis = [...PartnerUniData];
  if (pathway === "local") unis = unis.filter((u) => u.type === "local");
  if (pathway === "overseas") unis = unis.filter((u) => u.type === "overseas");
  if (budget === "below_20k") unis = unis.filter((u) => u.type === "local");
  // Filter by interest fields (loose match)
  const interestLabels = interests.map(
    (id) => INTERESTS.find((i) => i.id === id)?.label.split(" /")[0] || "",
  );
  const matched = unis.filter((u) =>
    u.fields.some((f) =>
      interestLabels.some(
        (il) =>
          f.toLowerCase().includes(il.toLowerCase()) ||
          il.toLowerCase().includes(f.toLowerCase()),
      ),
    ),
  );
  const result = matched.length >= 2 ? matched : unis;
  return result.slice(0, 4);
}

function buildWhatsAppLink(answers: Answers): string {
  const interest =
    answers.interests
      .map((id) => INTERESTS.find((i) => i.id === id)?.label || id)
      .join(", ") || "Not specified";
  const qual =
    QUALIFICATIONS.find((q) => q.id === answers.qualifications)?.label ||
    answers.qualifications ||
    "Not specified";
  const result = answers.resultRange || "Not specified";
  const budget =
    BUDGETS.find((b) => b.id === answers.budget)?.label ||
    answers.budget ||
    "Not specified";
  const msg = `Hi SCS Education, I used the Course Finder. I'm interested in ${interest}. My qualification is ${qual}. My result range is ${result}. My budget is ${budget}. Please help me check suitable courses and universities.`;
  return `https://wa.me/601112870256?text=${encodeURIComponent(msg)}`;
}

export function getReasons(uni: PartnerUniProps, answers: Answers): string[] {
  const reasons: string[] = [];
  if (uni.type.toLowerCase() === answers.pathway.toLowerCase())
    reasons.push("Matches your preferred location");
  if (
    uni.fields.some((field) =>
      answers.interests.some(
        (interest) =>
          field.toLowerCase() ===
            INTERESTS.find((i) => i.id === interest)?.label.toLowerCase() || "",
      ),
    )
  )
    reasons.push("Matches your preferred field of study");
  if (uni.feeRange.toLowerCase() === answers.budget.toLowerCase())
    reasons.push("Matches your preferred budget");
  return reasons;
}

// ─── RESULTS SCREEN ─────────────────────────────────────────────────────────
export default function ResultsScreen({
  answers,
  onGoToDashboard,
}: {
  answers: Answers;
  onGoToDashboard: () => void;
}) {
  const courses = getRecommendedCourses(answers.interests);
  const unis = getRecommendedUniversities(
    answers.pathway,
    answers.budget,
    answers.interests,
  );
  const waLink = buildWhatsAppLink(answers);
  const [askedCourses, setAskedCourses] = useState<Set<string>>(new Set());
  const [askedUnis, setAskedUnis] = useState<Set<string>>(new Set());

  const interestLabels = answers.interests.map(
    (id) => INTERESTS.find((i) => i.id === id)?.label || id,
  );
  const qualLabel =
    QUALIFICATIONS.find((q) => q.id === answers.qualifications)?.label ||
    answers.qualifications;
  const budgetLabel =
    BUDGETS.find((b) => b.id === answers.budget)?.label || answers.budget;
  const pathwayLabel =
    PATHWAYS.find((p) => p.id === answers.pathway)?.label || answers.pathway;
  const timelineLabel =
    TIMELINES.find((t) => t.id === answers.timeline)?.label || answers.timeline;

  const summaryItems = [
    {
      icon: <GraduationCap className="w-3.5 h-3.5" />,
      label: "Qualification",
      value: qualLabel,
    },
    {
      icon: <BookOpen className="w-3.5 h-3.5" />,
      label: "Result Range",
      value: answers.resultRange || "Not specified",
    },
    {
      icon: <MapPin className="w-3.5 h-3.5" />,
      label: "Study Option",
      value: pathwayLabel,
    },
    {
      icon: <DollarSign className="w-3.5 h-3.5" />,
      label: "Budget",
      value: budgetLabel,
    },
    {
      icon: <Clock className="w-3.5 h-3.5" />,
      label: "Timeline",
      value: timelineLabel,
    },
  ];

  return (
    <div className="pb-16">
      {/* Hero banner */}
      <div className="bg-gradient-to-br from-violet-600 to-purple-700 rounded-3xl p-6 text-white mb-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <p
              className="text-[11px] text-violet-200 uppercase tracking-widest mb-1"
              style={{ fontWeight: 700 }}
            >
              Your Results
            </p>
            <h2
              className="text-[22px] text-white tracking-tight mb-1"
              style={{ fontWeight: 800 }}
            >
              Your Possible Study Directions
            </h2>
            <p
              className="text-[13px] text-violet-200 leading-relaxed"
              style={{ fontWeight: 400 }}
            >
              Based on your answers, here are some course directions and
              universities you may want to explore. A counsellor will confirm
              entry requirements, fees and scholarship options.
            </p>
          </div>
        </div>
      </div>

      {/* Summary card */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-5 mb-6">
        <p
          className="text-[13px] text-slate-700 mb-4"
          style={{ fontWeight: 700 }}
        >
          Your Answers Summary
        </p>
        {interestLabels.length > 0 && (
          <div className="mb-3">
            <p
              className="text-[11px] text-slate-400 mb-1.5"
              style={{ fontWeight: 500 }}
            >
              Study Interests
            </p>
            <div className="flex flex-wrap gap-1.5">
              {interestLabels.map((l) => (
                <span
                  key={l}
                  className="px-2.5 py-1 rounded-full bg-violet-50 border border-violet-100 text-[12px] text-violet-700"
                  style={{ fontWeight: 600 }}
                >
                  {l}
                </span>
              ))}
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {summaryItems
            .filter((s) => s.value)
            .map((item, i) => (
              <div key={i} className="bg-slate-50 rounded-2xl p-3">
                <div className="flex items-center gap-1.5 text-slate-400 mb-1">
                  {item.icon}
                  <span className="text-[10px]" style={{ fontWeight: 600 }}>
                    {item.label.toUpperCase()}
                  </span>
                </div>
                <p
                  className="text-[12px] text-slate-800"
                  style={{ fontWeight: 600 }}
                >
                  {item.value}
                </p>
              </div>
            ))}
        </div>
      </div>

      {/* Section A: Course Directions */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-xl bg-violet-100 flex items-center justify-center">
            <BookOpen className="w-3.5 h-3.5 text-violet-600" />
          </div>
          <h3
            className="text-[16px] text-slate-900"
            style={{ fontWeight: 700 }}
          >
            Possible Course Directions
          </h3>
        </div>
        <p
          className="text-[12px] text-slate-400 mb-4"
          style={{ fontWeight: 400 }}
        >
          These are directions you may want to explore. A counsellor will
          confirm entry requirements.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          {courses.map((course, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07 }}
              className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center flex-shrink-0">
                  <GraduationCap className="w-3.5 h-3.5 text-violet-600" />
                </div>
                {askedCourses.has(course.name) && (
                  <span
                    className="text-[10px] text-green-600 bg-green-50 px-2 py-0.5 rounded-full border border-green-200 flex items-center gap-1"
                    style={{ fontWeight: 600 }}
                  >
                    <Check className="w-2.5 h-2.5" /> Sent to counsellor
                  </span>
                )}
              </div>
              <p
                className="text-[14px] text-slate-900 mb-1"
                style={{ fontWeight: 700 }}
              >
                {course.name}
              </p>
              <p
                className="text-[12px] text-slate-500 mb-3 leading-relaxed"
                style={{ fontWeight: 400 }}
              >
                {course.desc}
              </p>
              {!askedCourses.has(course.name) ? (
                <a
                  href={`https://wa.me/601112870256?text=${encodeURIComponent(`Hi SCS Education, I'm interested in ${course.name}. Can you help me check this course?`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    setAskedCourses((prev) => new Set([...prev, course.name]))
                  }
                  className="text-[12px] text-violet-600 flex items-center gap-1 hover:text-violet-700 transition-colors"
                  style={{ fontWeight: 600 }}
                >
                  Ask SCS Education to check this for me{" "}
                  <ChevronRight className="w-3 h-3" />
                </a>
              ) : null}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Section B: Universities */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-xl bg-orange-100 flex items-center justify-center">
            <Star className="w-3.5 h-3.5 text-orange-600" />
          </div>
          <h3
            className="text-[16px] text-slate-900"
            style={{ fontWeight: 700 }}
          >
            Universities to Consider
          </h3>
        </div>
        <p
          className="text-[12px] text-slate-400 mb-4"
          style={{ fontWeight: 400 }}
        >
          Universities SCS Education can help you compare. Fees and entry
          requirements will be confirmed by a counsellor.
        </p>
        <div className="flex flex-col gap-3">
          {unis.map((uni, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + i * 0.07 }}
              className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 hover:shadow-md transition-all duration-200"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 overflow-hidden flex-shrink-0 shadow-md border border-slate-100 bg-slate-50">
                  <img
                    src={uni.uniLogo}
                    alt={uni.uniName}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="text-[14px] text-slate-900"
                    style={{ fontWeight: 700 }}
                  >
                    {uni.uniName}
                  </p>
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    <MapPin className="w-3 h-3 text-slate-400" />
                    <span
                      className="text-[11px] text-slate-500"
                      style={{ fontWeight: 400 }}
                    >
                      {uni.location}
                    </span>
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full ${uni.type === "local" ? "bg-blue-50 text-blue-600" : "bg-orange-50 text-orange-600"}`}
                      style={{ fontWeight: 700 }}
                    >
                      {uni.type === "local" ? "Local" : "Overseas"}
                    </span>
                  </div>
                </div>
                {uni.scholarship && (
                  <span
                    className="text-[10px] text-green-700 bg-green-50 border border-green-100 px-2 py-0.5 rounded-full flex-shrink-0"
                    style={{ fontWeight: 600 }}
                  >
                    🎓 Scholarship available
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {/* {uni.reason.map((w, wi) => ( */}
                {getReasons(uni, answers).map((w, wi) => (
                  <span
                    key={wi}
                    className="text-[10px] px-2.5 py-1 rounded-full bg-violet-50 text-violet-600 border border-violet-100"
                    style={{ fontWeight: 600 }}
                  >
                    ✓ {w}
                  </span>
                ))}
              </div>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p
                    className="text-[11px] text-slate-400"
                    style={{ fontWeight: 400 }}
                  >
                    Estimated fee range
                  </p>
                  <p
                    className="text-[12px] text-slate-700"
                    style={{ fontWeight: 600 }}
                  >
                    {uni.feeRange}
                  </p>
                  <p
                    className="text-[10px] text-slate-400 mt-0.5"
                    style={{ fontWeight: 400 }}
                  >
                    Fees and entry requirements will be confirmed by a
                    counsellor.
                  </p>
                </div>
                {!askedUnis.has(uni.uniName) ? (
                  <a
                    href={`https://wa.me/601112870256?text=${encodeURIComponent(`Hi SCS Education, I'm interested in ${uni.uniName}. Can you help me check this university?`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      setAskedUnis((prev) => new Set([...prev, uni.uniName]))
                    }
                    className="flex-shrink-0 px-3 py-2 rounded-xl bg-violet-600 text-white text-[11px] hover:bg-violet-700 transition-colors flex items-center gap-1"
                    style={{ fontWeight: 600 }}
                  >
                    Check with counsellor <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <span
                    className="text-[11px] text-green-600 flex items-center gap-1"
                    style={{ fontWeight: 600 }}
                  >
                    <Check className="w-3 h-3" /> Sent!
                  </span>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Section C: Scholarship check */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl border border-orange-100 p-5 mb-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl bg-orange-100 flex items-center justify-center flex-shrink-0">
            <Star className="w-4 h-4 text-orange-600" />
          </div>
          <div className="flex-1">
            <h3
              className="text-[15px] text-slate-900 mb-1"
              style={{ fontWeight: 700 }}
            >
              Want us to check scholarships or rebates for you?
            </h3>
            <p
              className="text-[13px] text-slate-600 mb-3 leading-relaxed"
              style={{ fontWeight: 400 }}
            >
              SCS Education can help check available scholarships, rebates or
              fee support based on your result and chosen university.
            </p>
            <a
              href={`https://wa.me/601112870256?text=${encodeURIComponent(`Hi SCS Education, I would like to check scholarship and rebate options for my preferred courses and universities. My qualification is ${QUALIFICATIONS.find((q) => q.id === answers.qualifications)?.label || answers.qualifications}.`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-orange-500 text-white text-[13px] hover:bg-orange-600 transition-colors shadow-sm"
              style={{ fontWeight: 600 }}
            >
              Check Scholarship Options <ChevronRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>

      {/* Save / Go to Hub CTA */}
      <div className="bg-gradient-to-br from-violet-600 to-purple-700 rounded-3xl p-6 mb-6 text-white">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <User className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3
              className="text-[16px] text-white mb-1"
              style={{ fontWeight: 700 }}
            >
              Your Profile Has Been Created!
            </h3>
            <p
              className="text-[13px] text-violet-200 leading-relaxed"
              style={{ fontWeight: 400 }}
            >
              Your course preferences have been saved. A counsellor will review
              your details and reach out to help you with the next steps.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={onGoToDashboard}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-white text-violet-700 text-[13px] hover:bg-violet-50 transition-colors shadow-sm cursor-pointer"
            style={{ fontWeight: 700 }}
          >
            Go to My Student Hub <ArrowRight className="w-4 h-4" />
          </button>
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/20 text-white text-[13px] hover:bg-white/30 transition-colors"
            style={{ fontWeight: 600 }}
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp Us
          </a>
        </div>
      </div>

      {/* Overseas note */}
      {(answers.pathway === "overseas" || answers.pathway === "both") && (
        <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 mb-6">
          <p
            className="text-[12px] text-amber-800 leading-relaxed"
            style={{ fontWeight: 500 }}
          >
            ⚠️ <strong>Overseas options note:</strong> Overseas study may
            require additional checks for visa, English language requirements
            (e.g. IELTS/TOEFL), fees and intake deadlines. A counsellor will
            walk you through all the details.
          </p>
        </div>
      )}
    </div>
  );
}
